import { useAuth } from '@/contexts/auth-context';
import { isOnFreePlan } from '@/guards/free-plan-access';
import { useSubscriptionQuery } from '@/hooks/queries/use-subscription-query';
import { usePlanChoiceStore } from '@/stores/plan-choice-store';
import { usePaywall } from '@/hooks/use-paywall';
import { shouldOfferPlanChoice } from '@/components/subscription/plan-choice/plan-choice-offer';
import { useEffect, useRef } from 'react';

/**
 * Offers the plan choice once, to a free brand arriving on its dashboard from
 * its onboarding and paying for nothing. Nothing for any other brand.
 */
export const PlanChoiceGate = () => {
  const { user } = useAuth();
  const brandId = user?.currentBrandId ?? null;
  const decided = usePlanChoiceStore((state) =>
    brandId ? !!state.decidedAtByBrandId[brandId] : false
  );
  const markDecided = usePlanChoiceStore((state) => state.markDecided);
  const { paywall, openPlans } = usePaywall();
  const offeredBrand = useRef<string | null>(null);
  // The session does not carry the subscription: the current-subscription
  // read (outside the paywall) tells whether this free organization now pays.
  const subscription = useSubscriptionQuery({
    enabled: !!brandId && isOnFreePlan(user),
    scopeKey: brandId,
  });

  useEffect(() => {
    if (!brandId || subscription.isPending || offeredBrand.current === brandId) return;
    if (paywall) {
      offeredBrand.current = brandId;
      return;
    }
    if (
      !shouldOfferPlanChoice(user, {
        decided,
        hasActiveSubscription: subscription.data?.status === 'active',
      })
    )
      return;
    offeredBrand.current = brandId;
    openPlans('free', { onClose: () => markDecided(brandId) });
  }, [
    brandId,
    subscription.isPending,
    subscription.data?.status,
    user,
    decided,
    paywall,
    openPlans,
    markDecided,
  ]);

  return null;
};
